import type { ApiClientErrorCode, PartnerListItem } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { PARTNER_CITIES } from './cities';
import { RASPIL_SUBCATEGORIES } from './categories';

/**
 * Presenter Экрана 2.8.2 «Список партнёров категории» (Блок 8, ADR-0080).
 *
 * Чистая функция props → JSX. Порядок (прототип `ScreenList`): заголовок-категория
 * → селект «Город» (20 городов, дефолт Астана) → подтумблер подкатегорий ТОЛЬКО для
 * «Распил…» (вертикально, P-019) → тело по состоянию. 5 состояний (P-021):
 * idle / loading / success (карточки) / empty («Скоро появятся») / error.
 * Гейт «Распил…»: при `needsSubcategory` тело-состояние замещается подсказкой
 * «Выберите подкатегорию» (запрос ещё не выполнялся).
 *
 * P-040: VideoButton тут НЕ дублируется — это ДОЧЕРНИЙ экран входа блока (2.8.1
 * PartnersCategories, где VideoButton первым). Прецедент — FurnitureSearchView.
 *
 * Карточка списка = название + адрес (только заполненные поля, разрежённо).
 * Кнопки мессенджеров в списке — НЕ здесь (Фаза D3). Тап по карточке → детальная.
 *
 * Дизайн-система: bg-canvas/bg-card/text-ink (P-038/P-039), Manrope (P-043),
 * палитра только через токены (P-045), жёлтый — заливка/обводка, не текст (P-041).
 * Layout shell P-046, вертикальные элементы P-019, без языкопереключателя (P-020).
 */
export type PartnersListState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface PartnersListViewProps {
  /** Заголовок экрана: канон-имя категории, либо объединение «A / B» в merged-режиме. */
  category: string;
  state: PartnersListState;
  /** Выбранный город (дефолт «Астана»). */
  city: string;
  onCityChange: (city: string) => void;
  /** true → показать подтумблер подкатегорий «Распил…». */
  showSubcategories: boolean;
  /**
   * Гейт «Распил…»: true → подкатегория ещё не выбрана. Вместо тела-состояния
   * показываем подсказку «Выберите подкатегорию» (запрос не выполняется). Для прочих
   * категорий и merged-режима всегда false.
   */
  needsSubcategory: boolean;
  /** Выбранная подкатегория (undefined = все, по умолчанию). */
  subcategory?: string;
  onSubcategoryChange: (next: string | undefined) => void;
  items?: readonly PartnerListItem[];
  error?: { code: ApiClientErrorCode; message: string };
  onItemClick: (id: string) => void;
  /**
   * F2: валидированный (внутренний) `returnTo` → «Назад» ведёт в калькулятор-анкету
   * (R3). `undefined` → обычное «Назад» по истории (P-036).
   */
  backTo?: string;
}

export function PartnersListView(props: PartnersListViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Партнёры" />

      <section className="flex-1 overflow-y-auto flex flex-col gap-4 p-6">
        {/* Заголовок-категория (h2; «Партнёры» — h1 в Header, не дублируем) */}
        <h2 className="font-display text-h2 text-ink m-0">{props.category}</h2>

        {/* Селект «Город» (P-021 общий фильтр; без языкопереключателя P-020) */}
        <div className="flex flex-col gap-2">
          <label htmlFor="partners-city" className="text-ink text-base font-body font-semibold">
            Город
          </label>
          <select
            id="partners-city"
            value={props.city}
            onChange={(e) => props.onCityChange(e.target.value)}
            aria-label="Город"
            className={[
              'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
              'text-[18px] px-4 py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
            ].join(' ')}
          >
            {PARTNER_CITIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Подтумблер подкатегорий ТОЛЬКО для «Распил…» (вертикально, P-019) */}
        {props.showSubcategories && (
          <div className="flex flex-col gap-2">
            <span className="text-ink text-base font-body font-semibold">Подкатегория</span>
            {RASPIL_SUBCATEGORIES.map((s) => {
              const selected = props.subcategory === s;
              return (
                <button
                  key={s}
                  type="button"
                  aria-label={s}
                  aria-pressed={selected}
                  onClick={() => props.onSubcategoryChange(selected ? undefined : s)}
                  className={[
                    'w-full min-h-tap rounded-xl border-[1.5px] border-accent text-ink',
                    'font-body text-base font-medium text-left px-4 py-3 shadow-card',
                    selected ? 'bg-accent' : 'bg-card',
                    'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
                  ].join(' ')}
                >
                  {s}
                </button>
              );
            })}
          </div>
        )}

        {/* Гейт «Распил…»: пока подкатегория не выбрана — нейтральная подсказка вместо
            тела-состояния (запрос не идёт). Иначе — обычные 5 состояний (P-021). */}
        {props.needsSubcategory ? (
          <Centered text="Выберите подкатегорию" tone="muted" />
        ) : (
          <StateView {...props} />
        )}
      </section>

      <BottomBar backTo={props.backTo} />
    </main>
  );
}

function StateView(props: PartnersListViewProps) {
  switch (props.state) {
    case 'idle':
      return <Centered text="Выберите параметры выше." tone="muted" />;
    case 'loading':
      return <Centered text="Загружаем…" tone="muted" />;
    case 'empty':
      // empty (нет партнёров по фильтру: не-Астана город / подкатегория без данных).
      return <Centered text="Скоро появятся" tone="muted" />;
    case 'error':
      return (
        <Centered
          text="Не удалось загрузить. Проверьте соединение и попробуйте снова."
          tone="error"
        />
      );
    case 'success':
      return <PartnerCards {...props} />;
  }
}

function PartnerCards(props: PartnersListViewProps) {
  const items = props.items ?? [];
  return (
    <ul className="flex flex-col gap-3 list-none m-0 p-0">
      {items.map((item) => (
        <li key={item.id}>
          {/* Тап-карточка: название + адрес (только заполненные поля, разрежённо).
              Мессенджеры — Фаза D3. */}
          <button
            type="button"
            onClick={() => props.onItemClick(item.id)}
            aria-label={item.name}
            className={[
              'w-full min-h-tap flex flex-col rounded-xl bg-card border-[1.5px] border-accent',
              'text-left px-4 py-3 shadow-card',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            <span className="text-ink text-lg font-body font-semibold">{item.name}</span>
            {item.address && <span className="text-muted text-sm mt-1">{item.address}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

function Centered({ text, tone }: { text: string; tone: 'muted' | 'error' }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[30vh] gap-3 text-center">
      <p
        className={[
          tone === 'error' ? 'text-error' : 'text-muted',
          'text-[18px] max-w-[300px] m-0 leading-normal',
        ].join(' ')}
      >
        {text}
      </p>
    </div>
  );
}
